import React from 'react';
import { 
  Bot, Activity, Check, X, Sliders, BookOpen, Brain, CheckSquare, 
  AlertTriangle, ListOrdered, Zap, Star, Scale, CheckCircle, Compass, 
  GitMerge, Megaphone, Heart, MessageSquare, MapPin, Flag, Eye
} from 'lucide-react';
import { Panel, ValueBadge, LABEL } from './CandidateShared';

// --- SUB-COMPONENT: Generative Script ---
const GenerativeScript = ({ wardRisk }) => {
  if (!wardRisk) return null;
  let script = "No specific pivot script generated for this context.";
  let scriptRationale = "No THEIA-diagnosed authenticity gaps.";
  const authRisk = (wardRisk.risks_identified || []).find(r => r.source === 'MINERVA (THEIA)' && r.risk.includes('Authenticity Gap'));

  if (authRisk) {
    script = authRisk.recommendation || script;
    scriptRationale = authRisk.details || scriptRationale;
  }
  
  return (
    <div className="pt-4 mt-4 border-t border-slate-700">
      <h4 className="font-semibold text-white mb-3 flex items-center"><Bot size={16} className="mr-2 text-purple-400" /> Authenticity Pivot Script (THEIA)</h4>
      <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-700">
        <p className="text-sm text-slate-200 italic">"{script}"</p>
      </div>
      <p className="text-xs text-slate-500 mt-2"><strong>Rationale:</strong> {scriptRationale}</p>
    </div>
  );
};

// --- SUB-COMPONENT: Metacognitive Inoculation ---
const MetacognitiveInoculation = ({ wardRisk, candidateProfile }) => {
  if (!wardRisk || !candidateProfile) return null;

  let conflict = "This task is broadly aligned with the candidate's core psychological profile.";
  let inoculation = "Standard pre-briefing is sufficient. Reinforce the 'Natural Synergy' points.";
  let framework = "Identity Consistency"; 
  let mechanism = "Self-Verification";

  const authRisk = (wardRisk.risks_identified || []).find(r => r.source === 'MINERVA (THEIA)' && r.risk.includes('Authenticity Gap'));

  if (authRisk) {
    const isHighAgreeableness = (candidateProfile.personality_profile?.agreeableness || 0) > 0.7;
    const isConsensusBuilder = (candidateProfile.communication_style?.consensus_building || 0) > 0.7;
    conflict = `An authenticity gap has been identified: "${authRisk.details}".`;

    if (isHighAgreeableness && isConsensusBuilder) {
      conflict += ` Your high 'Agreeableness' will resist executing the required pivot.`;
      framework = "Self-Determination Theory (Ryan & Deci)";
      mechanism = "Autonomy Restoration via Value Alignment";
    } else {
      framework = "Cognitive Dissonance Theory (Festinger)";
      mechanism = "Transcendent Consistency";
    }
    
    const pivot = authRisk.recommendation || "follow the recommended strategy";
    const coreBelief = candidateProfile.core_beliefs || "Evidence-based policy";
    inoculation = `You must reframe this task. Do not think "I am attacking". Instead, think: "I am using my core belief in '${coreBelief}' to bridge this gap. My action is an act of principled courage to execute: *${pivot}*"`;
  }

  return (
    <Panel title="Metacognitive Inoculation (Private)" icon={Sliders}>
      <div className="space-y-4">
        <div className="flex items-center space-x-2 mb-2">
           <BookOpen size={14} className="text-slate-400" />
           <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Applied Framework: {framework}</span>
        </div>
        <div>
          <h4 className="font-semibold text-white mb-2 flex items-center"><AlertTriangle size={16} className="mr-2 text-red-400" /> Psychological Conflict</h4>
          <p className="text-sm text-slate-300">{conflict}</p>
        </div>
         <div className="p-3 bg-slate-900/40 rounded border border-slate-700/50">
           <div className="flex items-start space-x-2">
              <Brain size={14} className="text-purple-400 mt-0.5" />
              <div>
                <strong className="text-purple-400 text-xs block mb-1">Psychological Mechanism: {mechanism}</strong>
                <p className="text-xs text-slate-400">{authRisk ? "Reframing reduces cognitive cost." : "Reinforcing alignment increases persistence."}</p>
              </div>
           </div>
        </div>
        <div className="pt-4 border-t border-slate-700">
          <h4 className="font-semibold text-white mb-2 flex items-center"><CheckSquare size={16} className="mr-2 text-green-400" /> Cognitive Inoculation</h4>
          <p className="text-sm text-slate-300 whitespace-pre-line">{inoculation}</p>
        </div>
      </div>
    </Panel>
  );
};

// --- SUB-COMPONENT: Psychological Portrait ---
const PsychologicalPortrait = ({ profile }) => {
  if (!profile) return null;
  const insights = [];
  if (profile.extraversion < 0.5) insights.push("Reserved and analytical."); else insights.push("Energetic and outgoing.");
  if (profile.openness < 0.4) insights.push("Prefers established methods."); else insights.push("Highly creative.");
  if (profile.conscientiousness > 0.8) insights.push("Extremely diligent planner."); else insights.push("Spontaneous and flexible.");
  
  return (
    <div className="mt-6 bg-slate-900/50 p-4 rounded-lg border border-slate-700">
      <h4 className="font-semibold text-white flex items-center mb-3"><Eye size={16} className="mr-2 text-purple-400"/> Psychological Portrait</h4>
      <ul className="space-y-2 list-disc list-inside text-sm text-slate-300 pl-2">{insights.map((insight, i) => <li key={i}>{insight}</li>)}</ul>
    </div>
  );
};

const MessagingTab = ({ 
  selectedWardCode, 
  selectedWardName, 
  selectedWardNarrative, 
  selectedWardRisk, 
  profile, 
  isCoachView, 
  northStarNarrative, 
  narrative,
  communication_persona 
}) => {
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* LEFT COLUMN */}
      <div className="lg:col-span-2 space-y-8">
        
        {/* COACH VIEW */}
        {isCoachView && (
          <div className="border-b border-purple-500/30 pb-8 mb-8">
             {selectedWardCode ? (
                <MetacognitiveInoculation wardRisk={selectedWardRisk} candidateProfile={profile} />
              ) : (
                <Panel title="Metacognitive Inoculation (Private)" icon={Sliders}>
                  <p className="text-slate-400 text-sm">Select a ward in the "Command Center" above to generate private psychological analysis.</p>
                </Panel>
              )}
          </div>
        )}

        {/* CONTEXTUAL PLAYBOOK */}
        {selectedWardCode && selectedWardName ? (
            <Panel title={`Actionable Playbook: ${selectedWardName}`} icon={ListOrdered}>
            {selectedWardNarrative ? (
                <div className="space-y-4">
                <div className="mb-2"><ValueBadge value={selectedWardNarrative.candidate_compatibility_level} /></div>
                <div>
                    <h4 className="font-semibold text-white mb-2">Recommended Angles</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-slate-300 pl-4">
                    {(selectedWardNarrative.recommended_angles || []).map((angle, i) => <li key={i}>{angle}</li>)}
                    </ul>
                </div>
                {selectedWardNarrative.minerva_insight && (
                    <div className="pt-4 border-t border-slate-700">
                    <h4 className="font-semibold text-white mb-2 flex items-center"><Zap size={16} className="mr-2 text-yellow-400" /> Key Strategic Insight</h4>
                    <p className="text-sm text-slate-300">{selectedWardNarrative.minerva_insight}</p>
                    </div>
                )}
                <GenerativeScript wardRisk={selectedWardRisk} candidateProfile={profile} />
                </div>
            ) : <p className="text-slate-500">No specific messaging available for this ward.</p>}
            </Panel>
        ) : (
             <div className="p-6 rounded-lg bg-slate-800/50 border border-slate-700 border-dashed text-center text-slate-500">
                  <p>Select a ward in the "Command Center" above to view specific messaging playbooks.</p>
             </div>
        )}

        <Panel title="North Star Narrative" icon={Star}>
            {northStarNarrative.narrative ? (
                <div className="space-y-4">
                <div className="flex items-center space-x-2"><span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Primary Frame:</span><ValueBadge value={northStarNarrative.moral_frame} /></div>
                <blockquote className="border-l-4 border-accent-400 pl-4 py-2 bg-slate-900/50 rounded-r-lg"><p className="text-lg italic text-slate-200">"{northStarNarrative.narrative}"</p></blockquote>
                </div>
            ) : <p className="text-slate-500">No North Star Narrative generated.</p>}
        </Panel>

        {/* --- RESTORED: VALUES-BASED STRATEGY GUIDE --- */}
        {/* Originally CandidateViewer1 Lines 956-1050 */}
        <Panel title="Values-Based Strategy Guide" icon={Compass}>
          <p className="text-xs text-slate-400 mb-4">
            Universal motivations that predict policy priorities and cross-cultural messaging.
          </p>
          {(() => {
            const values = profile.values_profile || {};
            const sortedValues = Object.entries(values).sort(([, a], [, b]) => b - a);
            const topValue = sortedValues[0];
            const secondValue = sortedValues[1];

            const valueStrategies = {
              power: { policy: "Infrastructure, economic development, leadership roles", pitch: "Emphasize capability, influence, and getting things done", risk: "Can appear self-serving if not balanced with service narrative" },
              achievement: { policy: "Economic growth, innovation, competitive advantages", pitch: "Focus on measurable results, goals, and success stories", risk: "May seem cold or calculating without compassion balance" },
              universalism: { policy: "Environmental protection, equality, social justice", pitch: "Broad welfare, 'we're all in this together', systemic change", risk: "Can be seen as idealistic or impractical by pragmatic voters" },
              benevolence: { policy: "Community services, helping vulnerable, local support", pitch: "Personal stories, direct help, 'neighbor helping neighbor'", risk: "May lack the 'strength' narrative needed in competitive races" },
              tradition: { policy: "Preserving heritage, respecting customs, local identity", pitch: "Continuity, wisdom of experience, protecting what matters", risk: "Can appear backward-looking or resistant to necessary change" },
              self_direction: { policy: "Education, innovation, freedom of choice, entrepreneurship", pitch: "Empowerment, independence, breaking from failed systems", risk: "May seem anarchic or anti-institutional to conservative voters" },
              security: { policy: "Public safety, economic stability, family protection", pitch: "Concrete safety measures, predictability, risk management", risk: "Can appear fear-based or overly cautious" },
              conformity: { policy: "Law and order, social cohesion, behavioral norms", pitch: "Respect, decorum, following established procedures", risk: "May seem authoritarian or inflexible" },
              stimulation: { policy: "Bold initiatives, experimental programs, disruption", pitch: "Excitement, novelty, 'shake things up' energy", risk: "Can appear reckless or unserious to risk-averse voters" },
              hedonism: { policy: "Quality of life, recreation, cultural amenities", pitch: "Enjoyment, satisfaction, making life better", risk: "May seem frivolous or lacking gravitas" }
            };

            const strategy1 = topValue && valueStrategies[topValue[0]];
            const strategy2 = secondValue && valueStrategies[secondValue[0]];

            return (
              <>
                {strategy1 && (
                  <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-700 mb-4">
                    <h4 className="font-semibold text-white mb-2 capitalize">Primary Driver: {topValue[0].replace('_', ' ')}</h4>
                    <div className="space-y-2 text-xs">
                      <div><span className="text-slate-400">Natural Policy Focus:</span><p className="text-slate-200 mt-1">{strategy1.policy}</p></div>
                      <div><span className="text-slate-400">Pitch Strategy:</span><p className="text-slate-200 mt-1">{strategy1.pitch}</p></div>
                      <div className="flex items-start space-x-2 mt-2 p-2 bg-yellow-900/20 border border-yellow-500/30 rounded">
                        <AlertTriangle size={12} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                        <div><span className="text-yellow-400 font-medium">Strategic Risk:</span><p className="text-slate-300 mt-1">{strategy1.risk}</p></div>
                      </div>
                    </div>
                  </div>
                )}
                
                {strategy2 && (
                    <div className="bg-slate-700/30 p-3 rounded-lg border border-slate-700">
                    <h4 className="font-medium text-white text-sm mb-2 capitalize">Secondary Driver: {secondValue[0].replace('_', ' ')}</h4>
                    <p className="text-xs text-slate-300">{strategy2.policy}</p>
                    </div>
                )}

                {/* Internal Value Tensions Logic (Restored) */}
                <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="font-semibold text-white mb-2 flex items-center text-sm"><GitMerge size={14} className="mr-2 text-blue-400" /> Internal Value Tensions</h4>
                    {(() => {
                    const tensions = [];
                    if (values.power > 0.6 && values.universalism > 0.6) tensions.push({ conflict: "Personal Influence vs. Collective Welfare", guidance: "Balance 'strong leadership' narrative with 'service to all' positioning" });
                    if (values.achievement > 0.7 && values.benevolence > 0.7) tensions.push({ conflict: "Personal Success vs. Helping Others", guidance: "Frame achievements as enabling greater community service" });
                    if (values.tradition > 0.6 && values.self_direction > 0.6) tensions.push({ conflict: "Respect for Customs vs. Need for Change", guidance: "Position as 'evolution not revolution' - updating while honoring roots" });
                    if (values.security > 0.7 && values.stimulation > 0.6) tensions.push({ conflict: "Safety/Stability vs. Bold Action", guidance: "Frame innovation as 'calculated risks for long-term security'" });

                    return tensions.length > 0 ? (
                        <div className="space-y-2">{tensions.map((t, i) => (<div key={i} className="bg-slate-800/50 p-2 rounded text-xs"><div className="text-yellow-400 font-medium">{t.conflict}</div><div className="text-slate-300 mt-1">→ {t.guidance}</div></div>))}</div>
                    ) : <p className="text-xs text-slate-400">Values are well-integrated. No significant internal conflicts detected.</p>;
                    })()}
                </div>
              </>
            );
          })()}
        </Panel>

        {/* --- RESTORED: MORAL MESSAGING FRAMEWORK (FULL) --- */}
        {/* Originally CandidateViewer1 Lines 875-935 */}
        <Panel title="Moral Messaging Framework" icon={Scale}>
          <p className="text-xs text-slate-400 mb-4">The candidate's authentic moral language. Use these frames for maximum credibility.</p>
          {(() => {
            const morals = Object.entries(profile.moral_foundations || {}).sort(([, a], [, b]) => b - a);
            const top3 = morals.slice(0, 3);
            const bottom2 = morals.slice(-2);

            const moralMessaging = {
              care: { frame: "Compassion & Protection", use: "Healthcare, social services, protecting vulnerable groups", avoid: "Cold policy details without human stories" },
              fairness: { frame: "Justice & Equality", use: "Economic equity, anti-corruption, proportional outcomes", avoid: "Purely emotional appeals without fairness logic" },
              loyalty: { frame: "Community & Belonging", use: "Local pride, 'our people', community protection", avoid: "Universal/cosmopolitan messaging that downplays local identity" },
              authority: { frame: "Order & Tradition", use: "Law-and-order, respect for institutions, established norms", avoid: "Revolutionary or anti-establishment rhetoric" },
              sanctity: { frame: "Integrity & Sacred Values", use: "Moral clarity, protecting what matters, principled positions", avoid: "Purely pragmatic/transactional framing" },
              liberty: { frame: "Freedom & Choice", use: "Individual rights, resistance to overreach, personal autonomy", avoid: "Heavy-handed regulation or 'big government' solutions" }
            };

            return (
              <>
                <div className="space-y-3 mb-4">
                  <h4 className="font-semibold text-white flex items-center"><CheckCircle size={14} className="mr-2 text-green-400"/> Primary Authentic Frames</h4>
                  {top3.map(([moral, score]) => {
                    const guide = moralMessaging[moral];
                    if (!guide) return null;
                    return (
                      <div key={moral} className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2"><strong className="text-green-400 text-sm">{guide.frame}</strong><ValueBadge value={LABEL(score)} /></div>
                        <p className="text-xs text-slate-300 mb-1"><strong>Lead with:</strong> {guide.use}</p>
                        <p className="text-xs text-slate-400"><strong>Avoid:</strong> {guide.avoid}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="pt-4 border-t border-slate-700">
                   <h4 className="font-semibold text-white flex items-center mb-3"><AlertTriangle size={14} className="mr-2 text-red-400"/> Low-Authenticity Frames (Avoid)</h4>
                   <div className="flex flex-wrap gap-2">
                     {bottom2.map(([moral]) => {
                        const guide = moralMessaging[moral];
                        return guide ? <span key={moral} className="px-2 py-1 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs">{guide.frame}</span> : null;
                     })}
                   </div>
                </div>
              </>
            );
          })()}
        </Panel>
      </div>

      {/* RIGHT COLUMN */}
      <aside className="space-y-8">
        <Panel title="Core Driver & Communication" icon={Megaphone}>
              <h4 className="font-semibold text-white mb-2 flex items-center"><Heart size={16} className="mr-2 text-green-400"/> Core Drivers</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {(profile.values_profile?.universalism > 0.7) && <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded-lg text-xs">Universalism</span>}
                {(profile.values_profile?.achievement > 0.7) && <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded-lg text-xs">Achievement</span>}
              </div>
              {communication_persona && (
                <div className="pt-4 border-t border-slate-700">
                  <h4 className="font-semibold text-white mb-2 flex items-center"><Brain size={16} className="mr-2 text-purple-400" /> Communication Persona</h4>
                  <p className="text-lg font-bold text-white">{communication_persona.comm_type || "Adaptive"}</p>
                  <p className="text-sm text-slate-300 mt-1">{communication_persona.insight}</p>
                </div>
              )}
              
              {/* --- RESTORED: NATURAL VS UNNATURAL STYLES --- */}
              {/* Originally CandidateViewer1 Lines 1115-1135 */}
              <h5 className="text-sm font-medium text-slate-300 mb-2 mt-4">Natural Styles (High Authenticity)</h5>
              <div className="flex flex-wrap gap-2 mb-4">
                {profile.communication_style?.detail_oriented > 0.8 && <span className="px-2 py-1 bg-blue-500/10 text-blue-300 rounded-lg text-xs">Detail-Oriented</span>}
                {profile.communication_style?.consensus_building > 0.7 && <span className="px-2 py-1 bg-blue-500/10 text-blue-300 rounded-lg text-xs">Consensus-Building</span>}
                {profile.communication_style?.principled_courage > 0.6 && <span className="px-2 py-1 bg-blue-500/10 text-blue-300 rounded-lg text-xs">Principled Courage</span>}
              </div>

              <h5 className="text-sm font-medium text-slate-300 mb-2">Unnatural Styles (Coaching Required)</h5>
              <div className="flex flex-wrap gap-2">
                {profile.communication_style?.narrative_storytelling < 0.3 && <span className="px-2 py-1 bg-orange-500/10 text-orange-300 rounded-lg text-xs">Narrative Storytelling</span>}
                {profile.communication_style?.emotional_appeal < 0.35 && <span className="px-2 py-1 bg-orange-500/10 text-orange-300 rounded-lg text-xs">Emotional Appeal</span>}
                {profile.communication_style?.confrontational < 0.25 && <span className="px-2 py-1 bg-orange-500/10 text-orange-300 rounded-lg text-xs">Confrontational</span>}
              </div>
              
              <PsychologicalPortrait profile={profile.personality_profile} />
        </Panel>

        <Panel title="Core Policy & Narrative" icon={Flag}>
              <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-700">
                    <h4 className="font-semibold text-white flex items-center mb-3"><ListOrdered size={16} className="mr-2 text-indigo-400"/> Key Policy Priorities</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-slate-300 pl-4">
                        {Object.entries(profile.policy_priorities || {}).map(([key, value]) => <li key={key} className="capitalize">{key.replace('_', ' ')} (Priority: {value})</li>)}
                    </ul>
              </div>
              <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-700 mt-4">
                    <h4 className="font-semibold text-white flex items-center mb-3"><Megaphone size={16} className="mr-2 text-red-400"/> Core Narrative Themes</h4>
                     {(narrative.core_narrative_themes || []).map((n, i) => (
                        <div key={i} className="border-l-2 border-red-500 pl-3 mb-2">
                            <strong className="text-white text-sm">{n.theme}</strong>
                            <p className="text-xs text-slate-400 mt-1">{n.message}</p>
                        </div>
                      ))}
              </div>
        </Panel>

        <Panel title="Communication Channels" icon={MapPin}>
              <ul className="space-y-3 text-sm">
                {(profile.communication_preferences || []).map((p, i) => (
                  <li key={i} className="text-sm border-l-2 border-purple-500 pl-3"><strong className="text-white">{p}</strong></li>
                ))}
              </ul>
        </Panel>
      </aside>
    </div>
  );
};

export default MessagingTab;